import ContentCopy from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Paper, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  useGetCodeBlock,
  useRunCodeBlock,
  useSetCodeBlockCode,
} from "../../../hook/codeBlock";
import { useTableFromFileId, useTables } from "../../../hook/table";
import { CodeBlock as CodeBlockBase } from "../../../types/codeBlock";
import { CodeBlock } from "../../../types/notebook/note/block/codeBlock";
import { CodeEditor } from "../../codeEditor";
import { NoteBlockContainer } from "../../note";
import { TableOptionsTable } from "../../tableOptionsTable";

export interface CodeBlockProps {
  block: CodeBlock;
}

export const CodeBlockCodeTab: React.FC<CodeBlockProps> = ({
  block: { "code-block-id": codeBlockId },
}) => {
  const getCodeBlock = useGetCodeBlock();
  const codeBlock = getCodeBlock(codeBlockId);
  const setCodeBlockCode = useSetCodeBlockCode();
  const runCodeBlock = useRunCodeBlock();

  const codeBlockToLogs = (block: CodeBlockBase | undefined) => {
    const res = block?.["execute-result"];
    const baseLogs = res?.logs ?? "";
    const errorLine = res?.error_line;
    const errorLineNumber = res?.error_line_number;
    const errorMessage = res?.message;
    const errorStackTrace = res?.stack_trace;
    const isError = res?.error ? true : false;
    const errorPortion = isError
      ? `\nERROR\n\n${errorLine}\n${errorMessage}\n${errorStackTrace}`
      : "";
    const fullLogs = `${baseLogs}${errorPortion}`;
    return fullLogs;
  };

  return (
    <CodeEditor
      code={codeBlock?.code ?? ""}
      logs={codeBlockToLogs(codeBlock)}
      writeCode={(code) => setCodeBlockCode({ codeBlockId, code })}
      runCode={(code) =>
        setCodeBlockCode({ codeBlockId, code }).then(() =>
          runCodeBlock(codeBlockId)
        )
      }
    />
  );
};

export const CodeBlockConnectionsTab: React.FC<CodeBlockProps> = ({
  block: { "code-block-id": codeBlockId },
}) => {
  const getCodeBlock = useGetCodeBlock();
  const codeBlock = getCodeBlock(codeBlockId);
  const tablesMap = useTables();
  const addOptions = Object.values(tablesMap);
  const tableFromFileId = useTableFromFileId();
  const fileConnections = codeBlock?.["file-connections"] ?? [];

  return (
    <Paper
      elevation={7}
      sx={{
        padding: "2rem",
        minWidth: "80rem",
        gap: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableOptionsTable fileIds={fileConnections} />
    </Paper>
  );
};

export const CodeBlockComponent: React.FC<CodeBlockProps> = React.memo((props) => {
  const {
    block: { "code-block-id": codeBlockId },
  } = props;
  const getCodeBlock = useGetCodeBlock();
  const codeBlock = getCodeBlock(codeBlockId);
  const [tab, setTab] = useState(0);

  return (
    <Box
      id={codeBlockId}
      sx={{ display: "flex", flexDirection: "row", flexGrow: 1 }}
    >
      <NoteBlockContainer
        name={codeBlock?.name ?? ""}
        description={codeBlock?.description ?? ""}
      >
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Typography>Code Block ID: {codeBlockId}</Typography>
          <IconButton
            color="primary"
            onClick={() => {
              navigator.clipboard.writeText(codeBlockId);
            }}
          >
            <ContentCopy />
          </IconButton>
        </Box>
        <Tabs value={tab}>
          <Tab color="primary" label={"Code"} onClick={() => setTab(0)} />
          <Tab
            color="primary"
            label={"Connections"}
            onClick={() => setTab(1)}
          />
        </Tabs>
        {tab === 0 ? (
          <CodeBlockCodeTab {...props} />
        ) : tab === 1 ? (
          <CodeBlockConnectionsTab {...props} />
        ) : (
          <>Error</>
        )}
      </NoteBlockContainer>
    </Box>
  );
});
